import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Alert, Stack, CircularProgress, Checkbox, Chip } from '@mui/material';
import { Add as AddIcon, DeleteOutlined as DeleteIcon, FilterListOutlined as FilterIcon, ListAltOutlined as EditIcon } from '@mui/icons-material';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import UserGroupModal from '../components/UserGroupModal';
import UserGroupDrawer from '../components/UserGroupDrawer';
import { UserAPI } from '../apis/UserAPI';
import { useAllApplicationsList, useRolesList } from '../components/ApplicationContext';
import { isAuthorized } from '../utils/utilities';
import authenticatedRoute from '../views/AuthenticatedRoute';
import { constants } from '../constants';
import { useThemeDetails } from '../components/ThemeDetailsContext';
const UserGroups = () => {
    const { stateImages } = useThemeDetails();
    const [userGroups, setUserGroups] = useState([]);
    const [applications, setApplications] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState('');
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
    // Modal and drawer state
    const [userGroupModalOpen, setUserGroupModalOpen] = useState(false);
    const [selectedUserGroup, setSelectedUserGroup] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userGroupToDelete, setUserGroupToDelete] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerGroup, setDrawerGroup] = useState(null);
    // Load data on component mount
    useEffect(() => {
        loadInitialData();
    }, []);
    const getAllApplications = useAllApplicationsList();
    const getRoles = useRolesList();
    const loadInitialData = async () => {
        setDataLoading(true);
        setError(null);
        try {
            // Load applications, user groups, and roles in parallel
            const [apps, userGroupsResponse, rolesResource] = await Promise.all([
                getAllApplications(),
                UserAPI.getUserGroups(),
                getRoles()
            ]);
            setApplications(apps.resource || apps);
            // Transform roles to ensure consistent format
            const rolesData = rolesResource;
            const transformedRoles = Array.isArray(rolesData)
                ? rolesData.map((role) => ({
                    id: role.role_id || role.id,
                    name: role.role_name || role.name || 'Unnamed Role'
                }))
                : [];
            setRoles(transformedRoles);
            // Transform user groups data
            const transformedGroups = userGroupsResponse.resource.map((group) => ({
                id: group.id,
                name: group.name || 'Unnamed Group',
                organization_id: group.organization_id,
                principal_type: group.principal_type,
                memberCount: group.memberCount || 0,
                dateAdded: group.metadata?.createdDate
                    ? new Date(group.metadata.createdDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    })
                    : 'Unknown',
                details: group.details
            }));
            setUserGroups(transformedGroups);
            // Set default application filter to first application if available
            const appList = apps.resource || apps;
            if (Array.isArray(appList) && appList.length > 0) {
                setSelectedApplication(appList[0].name);
            }
        }
        catch (err) {
            console.error('Failed to load data:', err);
            setError(constants.user_groups_error_load_data);
            setUserGroups([]);
        }
        finally {
            setDataLoading(false);
        }
    };
    const handleAddUserGroup = () => {
        if (!selectedApplication) {
            setError(constants.user_groups_select_app_before_create);
            return;
        }
        setSelectedUserGroup(null);
        setUserGroupModalOpen(true);
    };
    const handleOpenGroupDrawer = (group) => {
        setDrawerGroup(group);
        setDrawerOpen(true);
    };
    const handleEditUserGroup = (group) => {
        setSelectedUserGroup(group);
        setUserGroupModalOpen(true);
    };
    const handleDeleteUserGroup = (group) => {
        setUserGroupToDelete(group);
        setDeleteDialogOpen(true);
    };
    const handleSaveUserGroup = async (groupData) => {
        setLoading(true);
        setError(null);
        try {
            if (selectedUserGroup) {
                // Update existing user group
                await UserAPI.updateUserGroup(selectedUserGroup.id.toString(), {
                    name: groupData.name,
                    organization_id: selectedUserGroup.organization_id,
                    principal_type: selectedUserGroup.principal_type,
                    details: {
                        description: groupData.description || '',
                        roles: groupData.roleIds?.map(String) || []
                    }
                });
                // Update role assignments
                if (groupData.roleIds && groupData.roleIds.length > 0) {
                    await UserAPI.assignRolesToPrincipal(groupData.roleIds, selectedUserGroup.id, 2 // principal_type 2 = Group
                    );
                }
                setSuccessMessage(constants.user_groups_update_success ||
                    'User group updated successfully');
            }
            else {
                // Create new user group
                const selectedApp = applications.find((app) => app.name === selectedApplication);
                if (!selectedApp) {
                    throw new Error('No application selected');
                }
                const createdGroup = await UserAPI.createUserGroup({
                    name: groupData.name,
                    organization_id: selectedApp.organization_id,
                    principal_type: 2,
                    details: {
                        description: groupData.description || '',
                        roles: groupData.roleIds?.map(String) || []
                    }
                });
                // Assign roles to the newly created group (principal_type 2 = Group)
                if (groupData.roleIds &&
                    groupData.roleIds.length > 0 &&
                    createdGroup?.id) {
                    await UserAPI.assignRolesToPrincipal(groupData.roleIds, createdGroup.id, 2 // principal_type 2 = Group
                    );
                }
                setSuccessMessage(constants.user_groups_add_success);
            }
            // Reload data
            await loadInitialData();
            setUserGroupModalOpen(false);
            setSelectedUserGroup(null);
        }
        catch (err) {
            console.error('Failed to save user group:', err);
            setError(err instanceof Error
                ? err.message
                : constants.user_groups_error_save_group);
        }
        finally {
            setLoading(false);
        }
    };
    const confirmDeleteUserGroup = async () => {
        if (!userGroupToDelete)
            return;
        setLoading(true);
        setError(null);
        try {
            await UserAPI.deleteUserGroup(userGroupToDelete.id.toString());
            setSuccessMessage(constants.user_groups_delete_success);
            // Close dialog and drawer first, then reload data
            setDeleteDialogOpen(false);
            setUserGroupToDelete(null);
            setDrawerOpen(false);
            setDrawerGroup(null);
            await loadInitialData();
        }
        catch (err) {
            console.error('Failed to delete user group:', err);
            // Close dialog on error but do NOT reload data - row should stay
            setDeleteDialogOpen(false);
            setUserGroupToDelete(null);
            setError(err instanceof Error
                ? err.message
                : constants.user_groups_error_delete_group);
        }
        finally {
            setLoading(false);
        }
    };
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const allGroupIds = filteredGroups.map((group) => group.id);
            setSelectedGroups(allGroupIds);
        }
        else {
            setSelectedGroups([]);
        }
    };
    const handleSelectGroup = (groupId) => {
        setSelectedGroups((prev) => prev.includes(groupId)
            ? prev.filter((id) => id !== groupId)
            : [...prev, groupId]);
    };
    // Filter user groups based on selected application
    const filteredGroups = userGroups.filter((group) => {
        if (!selectedApplication)
            return true;
        // Find the application by name to get its organization_id
        const selectedApp = applications.find((app) => app.name === selectedApplication);
        if (!selectedApp)
            return false;
        // Check if the group's organization_id matches
        return (String(group.organization_id) ===
            String(selectedApp.organization_id));
    });
    // Bulk delete handler
    const handleBulkDelete = () => {
        setBulkDeleteDialogOpen(true);
    };
    const confirmBulkDeleteUserGroups = async () => {
        if (selectedGroups.length === 0)
            return;
        setBulkDeleteLoading(true);
        setError(null);
        try {
            await Promise.all(selectedGroups.map((groupId) => UserAPI.deleteUserGroup(groupId.toString())));
            setSuccessMessage(constants.user_groups_delete_success ||
                'Groups deleted successfully');
            setSelectedGroups([]);
            await loadInitialData();
            setBulkDeleteDialogOpen(false);
        }
        catch (err) {
            console.error('Failed to bulk delete user groups:', err);
            setError(err instanceof Error
                ? err.message
                : constants.user_groups_error_delete_group);
        }
        finally {
            setBulkDeleteLoading(false);
        }
    };
    // Check view permission
    if (!isAuthorized('RBAC:ViewUserGroup')) {
        return (_jsx(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: _jsxs(Box, { sx: { textAlign: 'center', py: 8 }, children: [_jsx(Typography, { variant: "h6", color: "#666", gutterBottom: true, children: constants.user_groups_access_denied_title }), _jsx(Typography, { variant: "body1", color: "#888", children: constants.user_groups_access_denied_desc })] }) }));
    }
    return (_jsxs(Box, { sx: { p: 3, bgcolor: '#f8f8f8', minHeight: '100vh' }, children: [_jsxs(Paper, { sx: { p: 3, borderRadius: 2, boxShadow: 'none' }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3
                        }, children: [_jsx(Typography, { variant: "body1", color: "#595959", children: constants.user_groups_header_description }), _jsxs(Box, { children: [!dataLoading &&
                                        !error &&
                                        selectedGroups.length > 0 &&
                                        isAuthorized('RBAC:DeleteUserGroup') && (_jsx(Button, { variant: "outlined", onClick: handleBulkDelete, disabled: bulkDeleteLoading, sx: {
                                            color: '#DA291C',
                                            border: '1px solid #DA291C',
                                            backgroundColor: 'transparent',
                                            boxShadow: 'none',
                                            mr: 1
                                        }, children: _jsx(DeleteIcon, {}) })), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: handleAddUserGroup, disabled: !isAuthorized('RBAC:CreateUserGroup'), sx: {
                                            bgcolor: '#26890d',
                                            textTransform: 'none',
                                            boxShadow: 'none',
                                            fontWeight: 600,
                                            '&:hover': {
                                                bgcolor: '#1f6f0a'
                                            }
                                        }, children: "Group" })] })] }), dataLoading && (_jsxs(Box, { sx: { textAlign: 'center', py: 4 }, children: [stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, {})), _jsx(Typography, { variant: "body2", sx: { mt: 2 }, children: constants.user_groups_loading })] })), error && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, children: error })), successMessage && (_jsx(Alert, { severity: "success", sx: { mb: 3 }, onClose: () => setSuccessMessage(''), children: successMessage })), !dataLoading && (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(Checkbox, { size: "small", checked: filteredGroups.length > 0 &&
                                                                selectedGroups.length ===
                                                                    filteredGroups.length, indeterminate: selectedGroups.length > 0 &&
                                                                selectedGroups.length <
                                                                    filteredGroups.length, onChange: handleSelectAll, sx: {
                                                                color: '#d9d9d9',
                                                                '&.Mui-checked': {
                                                                    color: '#26890d'
                                                                }
                                                            } }), _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                fontSize: '14px'
                                                            }, children: "Name" }), _jsx(FilterIcon, { sx: {
                                                                fontSize: 18,
                                                                color: '#26890d'
                                                            } })] }) }), _jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsx(Typography, { variant: "body2", sx: {
                                                        fontWeight: 600,
                                                        fontSize: '14px'
                                                    }, children: "# of people assigned" }) }), _jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsx(Typography, { variant: "body2", sx: {
                                                        fontWeight: 600,
                                                        fontSize: '14px'
                                                    }, children: "Role" }) }), _jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsx(Typography, { variant: "body2", sx: {
                                                        fontWeight: 600,
                                                        fontSize: '14px'
                                                    }, children: constants.common_table_header_action }) })] }) }), _jsx(TableBody, { children: filteredGroups.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 4, sx: {
                                                textAlign: 'center',
                                                py: 4
                                            }, children: _jsx(Typography, { variant: "body1", color: "#888", children: constants.user_groups_no_groups_found }) }) })) : (filteredGroups.map((group) => (_jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(Checkbox, { size: "small", checked: selectedGroups.includes(group.id), onChange: () => handleSelectGroup(group.id), sx: {
                                                                color: '#d9d9d9',
                                                                '&.Mui-checked': {
                                                                    color: '#26890d'
                                                                }
                                                            } }), _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                fontSize: '14px',
                                                                color: '#26890d',
                                                                textDecoration: 'underline',
                                                                cursor: 'pointer',
                                                                '&:hover': {
                                                                    textDecoration: 'underline'
                                                                }
                                                            }, onClick: () => handleOpenGroupDrawer(group), children: group.name })] }) }), _jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsx(Typography, { variant: "body2", sx: { fontSize: '14px' }, children: group.memberCount }) }), _jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsx(Box, { sx: {
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5
                                                    }, children: group.details?.roles &&
                                                        Array.isArray(group.details.roles) &&
                                                        group.details.roles.length >
                                                            0 ? (group.details.roles.map((roleId) => {
                                                        const role = roles.find((r) => String(r.id) ===
                                                            String(roleId));
                                                        return role ? (_jsx(Chip, { label: role.name, size: "small", sx: {
                                                                backgroundColor: '#f3f8eb',
                                                                color: '#1a5d09',
                                                                fontFamily: 'Open Sans',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                height: 20
                                                            } }, roleId)) : null;
                                                    })) : (_jsx(Typography, { variant: "body2", sx: {
                                                            fontSize: '14px',
                                                            color: '#888'
                                                        }, children: "-" })) }) }), _jsx(TableCell, { sx: {
                                                    borderBottom: '1px solid #ebebeb',
                                                    py: 1
                                                }, children: _jsxs(Box, { sx: {
                                                        display: 'flex',
                                                        gap: 1
                                                    }, children: [isAuthorized('RBAC:UpdateUserGroup') && (_jsx(IconButton, { onClick: () => handleEditUserGroup(group), sx: {
                                                                color: '#26890d',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(38, 137, 13, 0.04)'
                                                                }
                                                            }, size: "small", children: _jsx(EditIcon, { fontSize: "small" }) })), isAuthorized('RBAC:DeleteUserGroup') && (_jsx(IconButton, { onClick: () => handleDeleteUserGroup(group), sx: {
                                                                color: '#da291c',
                                                                '&:hover': {
                                                                    bgcolor: 'rgba(218, 41, 28, 0.04)'
                                                                }
                                                            }, size: "small", children: _jsx(DeleteIcon, { fontSize: "small" }) }))] }) })] }, group.id)))) })] }) }))] }), _jsx(UserGroupModal, { open: userGroupModalOpen, onClose: () => {
                    setUserGroupModalOpen(false);
                    setSelectedUserGroup(null);
                }, onSave: handleSaveUserGroup, group: selectedUserGroup, loading: loading, applications: applications, roles: roles }), _jsx(UserGroupDrawer, { open: drawerOpen, onClose: () => {
                    setDrawerOpen(false);
                    setDrawerGroup(null);
                }, group: drawerGroup, onDelete: handleDeleteUserGroup, onMembersUpdated: loadInitialData }), _jsx(ConfirmationDialog, { isOpen: deleteDialogOpen, setIsOpen: () => {
                    setDeleteDialogOpen(false);
                    setUserGroupToDelete(null);
                }, submitFunction: confirmDeleteUserGroup, bodyText: constants.user_groups_delete_confirm_body(userGroupToDelete?.name ?? ''), submitButtonText: constants.delete, loading: loading }), _jsx(ConfirmationDialog, { isOpen: bulkDeleteDialogOpen, setIsOpen: () => {
                    setBulkDeleteDialogOpen(false);
                }, submitFunction: confirmBulkDeleteUserGroups, bodyText: selectedGroups.length === 0
                    ? 'No groups selected.'
                    : `Are you sure you want to delete ${selectedGroups.length} selected group(s)? This action cannot be undone.`, submitButtonText: constants.delete, loading: bulkDeleteLoading })] }));
};
export default authenticatedRoute(UserGroups);
